import { segment, ga } from '@mobi/libraries/analytics'
import isClient from 'utils/isClient'
import { formIdRegister } from './constants'
import { generateAndGetCorrelationId } from './correlationId'
import {
  createGA4PageView,
  createGA4ScreenView,
  createGA4Visualization,
  createGA4Click,
  createPageLoad,
} from './gaHelpers'
import ga4 from './ga4'
import {
  generateSegmentData,
  generateSegmentPageViewPayload,
} from './segmentHelpers'

const trackings = {
  elementClicked: ({
    location = '',
    position = '',
    elementType = '',
    text = '',
    htmlId = '',
    actionOutcome = '',
    outboundUrl = '',
    useCorrelationId = false,
  }) => {
    const correlationId = useCorrelationId ? generateAndGetCorrelationId() : ''
    const payload = {
      webElement: {
        location,
        position,
        elementType,
        text,
        htmlId,
      },
      actionOutcome,
      outboundUrl,
      correlationId,
    }

    segment.track('button_click', payload)
  },

  formViewed: ({ formName = 'debt_negotiation', formType = 'lead' }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    segment.track('form_viewed', payload)
  },

  formStarted: ({ formName = 'debt_negotiation', formType = 'lead' } = {}) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    segment.track('form_started', payload)
  },

  fieldInputted: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    fieldLabel = '',
    fieldId = '',
    fieldName = '',
    fieldType = '',
    fieldValue = 'pii filtered',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      userInputField: {
        fieldLabel,
        fieldId: fieldId?.toString(),
        fieldName,
        fieldType,
        fieldValue,
      },
    }

    segment.track('field_inputted', payload)
  },

  formContinued: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    stepName = '',
    stepType = '',
    field = [],
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      stepContext: {
        stepName,
        stepType,
      },
      field,
    }

    segment.track('form_continued', payload)
  },

  formBacked: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    stepName = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      stepContext: {
        stepName,
      },
    }

    segment.track('form_backed', payload)
  },

  fieldSelected: ({
    fieldType = 'UNKNOWN',
    fieldName = '',
    fieldId = '',
    fieldValue = '',
    fieldLabel = '',
    formName = 'debt_negotiation',
    formType = 'lead',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      userInputField: {
        fieldType,
        fieldName,
        fieldId: fieldId?.toString(),
        fieldValue,
        fieldLabel,
      },
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
    }

    segment.track('field_selected', payload)
  },

  formSubmitted: ({
    formName = 'debt_negotiation',
    formType = 'lead',
    field = [],
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      formContext: {
        formId: formIdRegister,
        formName,
        formType,
      },
      field,
    }

    segment.track('form_submitted', payload)
  },

  productViewed: ({
    productId = '',
    brand = '',
    location = '',
    category = '',
    position = '',
    price = '',
    text = '',
  }) => {
    const payload = {
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
      },
    }

    segment.track('product_viewed', payload)
  },

  productClicked: ({
    productId = '',
    brand = '',
    category = '',
    location = '',
    position = '',
    price = '',
    text = '',
    variant = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
        variant,
      },
    }

    segment.track('product_clicked', payload)
  },

  productAdded: ({
    productId = '',
    brand = '',
    category = '',
    location = '',
    position = null,
    price = null,
    text = '',
    variant = '',
  }) => {
    const correlationId = generateAndGetCorrelationId()
    const payload = {
      correlationId,
      product: {
        productId: productId?.toString(),
        brand,
        category,
        location,
        position,
        price,
        text,
        variant,
      },
    }

    segment.track('product_added', payload)
  },

  elementViewed: ({
    location = '',
    position = '',
    elementType = '',
    text = '',
    htmlId = '',
    useCorrelationId = false,
  }) => {
    const correlationId = useCorrelationId ? generateAndGetCorrelationId() : ''
    const payload = {
      webElement: {
        location,
        position,
        elementType,
        text,
        htmlId,
      },
      correlationId,
    }

    segment.track('element_viewed', payload)
  },

  LPPageView: () => {
    const pageData = generateSegmentPageViewPayload('LP')
    segment.track('lp_page_view', pageData)
  },
  segmentPageView: (pageName) => {
    const pageData = generateSegmentPageViewPayload(pageName)
    segment.page('page_view', pageData)
  },
  segmentVWOTest: ({ experimentId, variationName, pageName }) => {
    const defaultPayload = generateSegmentPageViewPayload(pageName)

    segment.track('VWO Test', {
      ...defaultPayload,
      experimentId,
      variationName,
      vwoUserId: isClient() && window?._vwo_uuid,
    })
  },
  segmentButtonClick: ({ name, id }) => {
    const segmentData = generateSegmentData()
    const payload = {
      ...segmentData,
      name,
      id,
    }

    segment.track('button_click', payload)
  },
  segmentElementViewed: (elementName) => {
    const segmentData = generateSegmentData()
    const payload = {
      ...segmentData,
      elementName,
    }

    segment.track('element_viewed', payload)
  },

  gaPageLoad: ({ pageName, pageData }) => {
    const payload = createPageLoad(pageName, pageData)
    const payloadGA4SV = createGA4ScreenView(pageName, pageData)
    const payloadGA4PV = createGA4PageView(pageName, pageData)

    ga.page(payload)
    ga4.track(payloadGA4SV)
    ga4.track(payloadGA4PV)
  },

  gaButtonClick: ({ id, text }) => {
    const payload = {
      events: {
        id: id,
        category: 'Clique',
        action: 'Button',
        label: `BTN:${text}`,
        noInteraction: 'false',
      },
      rule: 'customLink',
    }
    ga.track(payload)
  },

  gaCustomClick: (name) => {
    const payload = {
      events: {
        category: 'Clique',
        action: 'Simulador',
        label: `BTN:MonteSuaProposta:${name}`,
        noInteraction: 'false',
      },
      rule: 'customLink',
    }
    ga.track(payload)
  },

  gaVisualization: (name) => {
    const payload = {
      events: {
        category: 'Alerta',
        action: 'Visualização',
        label: name,
        noInteraction: 'false',
      },
      rule: 'customLink',
    }
    ga.track(payload)
  },

  ga4ScreenView: ({ pageName, pageData }) => {
    const payload = createGA4ScreenView(pageName, pageData)
    ga4.track(payload)
  },
  ga4PageView: ({ pageName, pageData }) => {
    const payload = createGA4PageView(pageName, pageData)
    ga4.track(payload)
  },

  ga4Visualization: ({ pageName, pageData }) => {
    const payload = createGA4Visualization(pageName, pageData)
    ga4.track(payload)
  },
  ga4Click: ({ pageName, pageData }) => {
    const payload = createGA4Click(pageName, pageData)
    ga4.track(payload)
  },
}

export default trackings
